import { useContext, useEffect, useState, type ReactNode } from 'react';
import { TextInput, Grid } from 'components/lib';
import { SimulationContext, type SubSimulation } from 'contexts/SimulationContext';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import GraphicEqOutlinedIcon from '@mui/icons-material/GraphicEqOutlined';
import AirOutlinedIcon from '@mui/icons-material/AirOutlined';
import { UMBetaBadge } from '@urbanmetrix/um-kit';
import { ValidationContext } from 'contexts/ValidationContext';
import { calculateCenterOfPolygon } from 'utils/calculateCenterOfPolygon';
import { calculateClosestStation } from 'services/calculateClosestStation';
import { ClosestStationContext } from 'contexts/ClosestStationContext';
import { ArrowRightIcon, ShadowIcon } from 'components/icons';
import { hydrateWindSubSimulationsWithStation } from 'services/windParamsHydration';
import { firstPolygon } from 'utils/multiPolygonHelper';

interface DefaultSubSims {
  solarSimulations: SubSimulation[];
  freeViewSimulations: SubSimulation[];
  targetViewSimulations: SubSimulation[];
  noiseSimulations: SubSimulation[];
  windSimulations: SubSimulation[];
  shadowSimulations: SubSimulation[];
}

interface NameProps {
  grid: { params?: { analysisArea?: unknown } | null } | null;
  isReviewMode?: boolean;
  mode?: 'full' | 'summary';
  onOpenAdvancedSettings?: (viewKey?: string) => void;
  onTargetViewEnabled?: () => void;
}

const summaryLabelStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontWeight: 600,
};

const summaryIconStyle = {
  fontSize: '1.15rem',
};

const Name = ({
  grid,
  isReviewMode = false,
  mode = 'full',
  onOpenAdvancedSettings,
  onTargetViewEnabled,
}: NameProps) => {
  const {
    simulation,
    setSimulation,
    subSimulations,
    setSubSimulations,
    defaultSolar,
    defaultFreeview,
    defaultNoise,
    defaultWind,
    defaultShadow,
  } = useContext(SimulationContext);

  const { isSimulationNameValid, setIsSimulationNameValid, simulationNameErrorMessage, setSimulationNameErrorMessage } =
    useContext(ValidationContext);

  const { closestStation, setClosestStation, setLoadingClosestStation, setInitialClosestStation } =
    useContext(ClosestStationContext);

  const [defaultSubSims, setDefaultSubSims] = useState<DefaultSubSims | null>(null);

  const buildDefaultSubSims = (subs: SubSimulation[] = []): DefaultSubSims => ({
    solarSimulations: subs.filter((subsim) => subsim.simType === 'SUN'),
    freeViewSimulations: subs.filter((subsim) => subsim.simType === 'FREEVIEW'),
    targetViewSimulations: subs.filter((subsim) => subsim.simType === 'TARGETVIEW'),
    noiseSimulations: subs.filter((subsim) => subsim.simType === 'NOISE'),
    windSimulations: subs.filter((subsim) => subsim.simType === 'WIND'),
    shadowSimulations: subs.filter((subsim) => subsim.simType === 'SHADOW'),
  });

  const getNextSubSimulationId = (subs: SubSimulation[] = []) =>
    subs.length > 0 ? Math.max(...subs.map((subSimulation) => subSimulation.id)) + 1 : 0;

  const buildFallbackSolarSimulations = (startId: number) => [
    defaultSolar(startId),
    {
      ...defaultSolar(startId + 1),
      name: 'Monthly Sun',
      params: {
        frameDivision: 'MONTHLY',
        raysEveryNMonths: 1,
        raysEveryNDays: 5,
        raysEveryNHours: 1,
        raysEveryNMinutes: 10,
      },
    },
    {
      ...defaultSolar(startId + 2),
      name: 'Hourly Sun',
      params: {
        frameDivision: 'HOURLY',
        raysEveryNMonths: 1,
        raysEveryNDays: 5,
        raysEveryNHours: 1,
        raysEveryNMinutes: 10,
      },
    },
  ];

  const buildFallbackFreeViewSimulations = (startId: number) => [
    defaultFreeview(startId),
    {
      ...defaultFreeview(startId + 1),
      name: 'Free View 180 2D',
      params: {
        decompose: true,
        raysLength: 100,
        raysResFactor: 50,
        outward: true,
        angUp: 0,
        angDown: 0,
      },
    },
  ];

  const getSectionSimulationsToRestore = (key: string, backup: DefaultSubSims): SubSimulation[] => {
    const nextId = getNextSubSimulationId([...subSimulations, ...Object.values(backup).flat()]);

    switch (key) {
      case 'solarEnabled':
        return backup.solarSimulations.length > 0 ? backup.solarSimulations : buildFallbackSolarSimulations(nextId);
      case 'freeViewEnabled':
        return backup.freeViewSimulations.length > 0
          ? backup.freeViewSimulations
          : buildFallbackFreeViewSimulations(nextId);
      case 'targetViewEnabled':
        return backup.targetViewSimulations;
      case 'noiseEnabled':
        return backup.noiseSimulations.length > 0 ? backup.noiseSimulations : [defaultNoise(nextId)];
      case 'windEnabled':
        return backup.windSimulations.length > 0 ? backup.windSimulations : [defaultWind(nextId, closestStation)];
      case 'shadowEnabled':
        return backup.shadowSimulations.length > 0 ? backup.shadowSimulations : [defaultShadow(nextId)];
      default:
        return [];
    }
  };

  const getSimulationCountForSection = (key: string) => {
    switch (key) {
      case 'solarEnabled':
        return subSimulations.filter((subsim) => subsim.simType === 'SUN').length;
      case 'freeViewEnabled':
        return subSimulations.filter((subsim) => subsim.simType === 'FREEVIEW').length;
      case 'targetViewEnabled':
        return subSimulations.filter((subsim) => subsim.simType === 'TARGETVIEW' && subsim.params?.enabled !== false)
          .length;
      case 'noiseEnabled':
        return subSimulations.filter((subsim) => subsim.simType === 'NOISE').length;
      case 'windEnabled':
        return subSimulations.filter((subsim) => subsim.simType === 'WIND').length;
      case 'shadowEnabled':
        return subSimulations.filter((subsim) => subsim.simType === 'SHADOW').length;
      default:
        return 0;
    }
  };

  /**()
   * Finds the closest station and sets the first wind simulation
   */
  useEffect(() => {
    const analysisArea = firstPolygon<{ x: number; y: number }>(grid?.params?.analysisArea);
    if (analysisArea.length === 0) return;

    try {
      const centerOfPolygon = calculateCenterOfPolygon(analysisArea);

      if (centerOfPolygon) {
        const calculatedStation = calculateClosestStation(centerOfPolygon);
        const hydratedSubSimulations = hydrateWindSubSimulationsWithStation(subSimulations, calculatedStation);
        const hasWindHydrationChanges = hydratedSubSimulations.some(
          (subSim: SubSimulation, index: number) => subSim !== subSimulations[index]
        );

        setClosestStation(calculatedStation);
        setInitialClosestStation(calculatedStation);
        setLoadingClosestStation(false);

        if (hasWindHydrationChanges) {
          setSubSimulations(hydratedSubSimulations);
          setDefaultSubSims((prevDefaultSubSims) =>
            prevDefaultSubSims
              ? {
                  ...prevDefaultSubSims,
                  windSimulations: hydratedSubSimulations.filter((subsim: SubSimulation) => subsim.simType === 'WIND'),
                }
              : prevDefaultSubSims
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [grid, setClosestStation, setInitialClosestStation, setLoadingClosestStation]);

  useEffect(() => {
    if (simulation?.name?.trim()) {
      setIsSimulationNameValid(true);
      setSimulationNameErrorMessage('');
    }
  }, [setIsSimulationNameValid, setSimulationNameErrorMessage, simulation?.name]);

  useEffect(() => {
    const nextDefaultSubSims = buildDefaultSubSims(subSimulations);
    if (
      !defaultSubSims ||
      (defaultSubSims.targetViewSimulations.length === 0 && nextDefaultSubSims.targetViewSimulations.length > 0)
    ) {
      setDefaultSubSims(nextDefaultSubSims);
    }
  }, [defaultSubSims, subSimulations]);

  const handleInputsChange = (name: string, value: string, _valid?: boolean) => {
    if (name === 'name') {
      if (value === '') {
        setIsSimulationNameValid(false);
        setSimulationNameErrorMessage('Please enter a simulation name!');
      } else {
        setIsSimulationNameValid(true);
        setSimulationNameErrorMessage('');
      }
    }

    if (name === 'description') {
      value = value.slice(0, 64);
    }

    setSimulation({ ...simulation, [name]: value });
  };

  const getIsSimulationSectionEnabled = (key: string) => {
    if (key === 'shadowEnabled') {
      return simulation.shadowEnabled;
    }

    if (key === 'targetViewEnabled') {
      if (simulation?.useStandardSettings) {
        return true;
      }

      return Boolean(simulation?.targetViewEnabled);
    }

    const hasSimulationsInSection = getSimulationCountForSection(key) > 0;

    if (simulation?.useStandardSettings) {
      return hasSimulationsInSection;
    }

    return hasSimulationsInSection && Boolean(simulation?.[key as keyof typeof simulation]);
  };

  const handleCheckboxChange = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    setSimulation((prevState) => ({
      ...prevState,
      useStandardSettings: false,
      solarEnabled: prevState.useStandardSettings ? true : prevState.solarEnabled,
      freeViewEnabled: prevState.useStandardSettings ? true : prevState.freeViewEnabled,
      targetViewEnabled: prevState.useStandardSettings ? true : prevState.targetViewEnabled,
      noiseEnabled: prevState.useStandardSettings ? true : prevState.noiseEnabled,
      windEnabled: prevState.useStandardSettings ? true : prevState.windEnabled,
      [key]: isChecked,
    }));

    let updatedSubSimulations = [...subSimulations]; // Create a copy of subSimulations to avoid mutating the original array
    const defaultSubSimulations = { ...(defaultSubSims ?? buildDefaultSubSims(subSimulations)) }; // Copy existing defaultSubSims (if any) to avoid mutations

    if (key === 'targetViewEnabled' && isChecked && defaultSubSimulations.targetViewSimulations.length === 0) {
      onTargetViewEnabled?.();
    }

    switch (key) {
      case 'solarEnabled':
        if (isChecked) {
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'SUN');
          updatedSubSimulations.push(...getSectionSimulationsToRestore(key, defaultSubSimulations));
        } else {
          const solarSims = updatedSubSimulations.filter((subsim) => subsim.simType === 'SUN');
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'SUN');
          defaultSubSimulations.solarSimulations = solarSims;
          setDefaultSubSims(defaultSubSimulations);
        }
        break;

      case 'freeViewEnabled':
        if (isChecked) {
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'FREEVIEW');
          updatedSubSimulations.push(...getSectionSimulationsToRestore(key, defaultSubSimulations));
        } else {
          const freeViewSims = updatedSubSimulations.filter((subsim) => subsim.simType === 'FREEVIEW');
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'FREEVIEW');
          defaultSubSimulations.freeViewSimulations = freeViewSims;
          setDefaultSubSims(defaultSubSimulations);
        }
        break;

      case 'targetViewEnabled':
        if (isChecked) {
          const targetViewSimulations = getSectionSimulationsToRestore(key, defaultSubSimulations);
          if (targetViewSimulations.length > 0) {
            updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'TARGETVIEW');
            updatedSubSimulations.push(...targetViewSimulations);
          }
        } else {
          const targetViewSims = updatedSubSimulations.filter((subsim) => subsim.simType === 'TARGETVIEW');
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'TARGETVIEW');
          defaultSubSimulations.targetViewSimulations = targetViewSims;
          setDefaultSubSims(defaultSubSimulations);
        }
        break;

      case 'noiseEnabled':
        if (isChecked) {
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'NOISE');
          updatedSubSimulations.push(...getSectionSimulationsToRestore(key, defaultSubSimulations));
        } else {
          const noiseSims = updatedSubSimulations.filter((subsim) => subsim.simType === 'NOISE');
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'NOISE');
          defaultSubSimulations.noiseSimulations = noiseSims;
          setDefaultSubSims(defaultSubSimulations);
        }
        break;

      case 'windEnabled':
        if (isChecked) {
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'WIND');
          updatedSubSimulations.push(...getSectionSimulationsToRestore(key, defaultSubSimulations));
        } else {
          const windSims = updatedSubSimulations.filter((subsim) => subsim.simType === 'WIND');
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'WIND');
          defaultSubSimulations.windSimulations = windSims;
          setDefaultSubSims(defaultSubSimulations);
        }
        break;

      case 'shadowEnabled':
        if (isChecked) {
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'SHADOW');
          updatedSubSimulations.push(...getSectionSimulationsToRestore(key, defaultSubSimulations));
        } else {
          const shadowSims = updatedSubSimulations.filter((subsim) => subsim.simType === 'SHADOW');
          updatedSubSimulations = updatedSubSimulations.filter((subsim) => subsim.simType !== 'SHADOW');
          defaultSubSimulations.shadowSimulations = shadowSims;
          setDefaultSubSims(defaultSubSimulations);
        }
        break;

      default:
        break;
    }
    setSubSimulations(updatedSubSimulations);
  };

  const hasAnyCustomSimulationSectionSelected = Boolean(
    getIsSimulationSectionEnabled('solarEnabled') ||
    getIsSimulationSectionEnabled('freeViewEnabled') ||
    simulation?.targetViewEnabled ||
    getIsSimulationSectionEnabled('noiseEnabled') ||
    getIsSimulationSectionEnabled('windEnabled') ||
    simulation.shadowEnabled
  );
  const shouldShowCustomSettingsSelectionError = !hasAnyCustomSimulationSectionSelected;
  const simulationGroups = [
    {
      key: 'solarEnabled',
      viewKey: 'solar',
      label: 'Solar',
      icon: <WbSunnyOutlinedIcon sx={summaryIconStyle} />,
      items: subSimulations
        .filter((subsim) => subsim.simType === 'SUN')
        .map((subsim) => ({ id: subsim.id, name: subsim.name || 'Sun' })),
    },
    {
      key: 'freeViewEnabled',
      viewKey: 'freeView',
      label: 'Free-view',
      icon: <VisibilityOutlinedIcon sx={summaryIconStyle} />,
      items: subSimulations
        .filter((subsim) => subsim.simType === 'FREEVIEW')
        .map((subsim) => ({ id: subsim.id, name: subsim.name || 'Free View' })),
    },
    {
      key: 'targetViewEnabled',
      viewKey: 'targetView',
      label: 'Target-view',
      icon: <MyLocationOutlinedIcon sx={summaryIconStyle} />,
      items: subSimulations
        .filter((subsim) => subsim.simType === 'TARGETVIEW' && subsim.params?.enabled !== false)
        .map((subsim) => ({ id: subsim.id, name: subsim.name || 'View' })),
    },
    {
      key: 'noiseEnabled',
      viewKey: 'noise',
      label: 'Noise',
      icon: <GraphicEqOutlinedIcon sx={summaryIconStyle} />,
      items: subSimulations
        .filter((subsim) => subsim.simType === 'NOISE')
        .map((subsim) => ({ id: subsim.id, name: subsim.name || 'Noise' })),
    },
    {
      key: 'windEnabled',
      viewKey: 'wind',
      label: 'Wind',
      icon: <AirOutlinedIcon sx={summaryIconStyle} />,
      items: subSimulations
        .filter((subsim) => subsim.simType === 'WIND')
        .map((subsim) => ({ id: subsim.id, name: subsim.name || 'Wind' })),
    },
    {
      key: 'shadowEnabled',
      viewKey: 'shadow',
      label: 'Shadow',
      icon: <ShadowIcon style={{ width: '1.15rem', height: '1.15rem' }} />,
      items: subSimulations
        .filter((subsim) => subsim.simType === 'SHADOW')
        .slice(0, 1)
        .map((subsim) => ({ id: subsim.id, name: subsim.name || 'Shadow Affecting Neighbors' })),
    },
  ];

  const renderSummaryLabel = (label: string, icon: ReactNode, badge?: ReactNode) => (
    <span style={summaryLabelStyle}>
      {icon}
      <span>{label}</span>
      {badge}
    </span>
  );

  if (mode === 'summary') {
    return (
      <div className="simulation-summary">
        <div className="simulation-summary__groups">
          {simulationGroups.map((group) => (
            <div key={group.key} className="simulation-summary__group">
              <FormControlLabel
                className={`checkbox-row ${
                  getIsSimulationSectionEnabled(group.key)
                    ? 'simulation-summary__group-label--enabled'
                    : 'simulation-summary__group-label--disabled'
                }`.trim()}
                onClick={(event) => {
                  event.preventDefault();
                  if (getIsSimulationSectionEnabled(group.key)) {
                    onOpenAdvancedSettings?.(group.viewKey);
                  }
                }}
                control={
                  <Checkbox
                    checked={getIsSimulationSectionEnabled(group.key)}
                    disabled={isReviewMode}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(e) => {
                      if (isReviewMode) {
                        return;
                      }
                      handleCheckboxChange(group.key, e);
                    }}
                  />
                }
                label={renderSummaryLabel(
                  group.label,
                  group.icon,
                  group.key === 'shadowEnabled' ? (
                    <span style={{ display: 'inline-flex' }}>
                      <UMBetaBadge className="simulation-summary__beta-badge" />
                    </span>
                  ) : null
                )}
              />

              {group.items.length > 0 && (
                <ul className="simulation-summary__sub-list">
                  {group.items.map((item) => (
                    <li key={`${group.key}-${item.id}`}>
                      <button
                        type="button"
                        className="simulation-summary__sub-button"
                        onClick={() => onOpenAdvancedSettings?.(group.viewKey)}
                      >
                        {item.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {shouldShowCustomSettingsSelectionError && mode !== 'summary' && (
          <p className="custom-error-message-selection">Select at least one simulation to continue.</p>
        )}

        {!shouldShowCustomSettingsSelectionError ? (
          <button
            type="button"
            className="simulation-summary__advanced-link"
            onClick={() => {
              onOpenAdvancedSettings?.();
            }}
          >
            <span>Advanced Settings</span>
            <ArrowRightIcon className="simulation-summary__link-icon" />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {!isReviewMode && (
        <Grid cols="3">
          <div>
            <TextInput
              label="Simulation Set Name"
              name="name"
              value={simulation?.name}
              onChange={handleInputsChange}
              required
            />
            {!isSimulationNameValid && <p className="custom-error-message">{simulationNameErrorMessage}</p>}
          </div>
          <TextInput
            label="Simulation Set Description"
            name="description"
            value={simulation?.description}
            onChange={handleInputsChange}
            maxLength={64}
          />
        </Grid>
      )}

      <RadioGroup
        value={simulation?.useStandardSettings}
        onChange={(e) => {
          const eTargetValue = e.target.value === 'true' ? true : false;

          setSimulation((prevState) => ({
            ...prevState,
            useStandardSettings: eTargetValue,
            nameStepEnabled: true,
            solarEnabled: !eTargetValue,
            freeViewEnabled: !eTargetValue,
            targetViewEnabled: !eTargetValue,
            noiseEnabled: !eTargetValue,
            windEnabled: !eTargetValue,
            reviewStepEnabled: true,
          }));
        }}
      >
        <FormControlLabel
          className="radio-row"
          value={true}
          control={<Radio />}
          label="Use standard settings for all simulations"
        />

        <FormControlLabel className="radio-row" value={false} control={<Radio />} label="Define custom settings" />
      </RadioGroup>

      {!simulation?.useStandardSettings && (
        <>
          <Typography variant="subtitle1" color="#1976d2" sx={{ mt: 1, mb: 1 }}>
            Simulations to be run:
          </Typography>

          <FormControlLabel
            className="checkbox-row"
            control={
              <Checkbox checked={simulation.solarEnabled} onChange={(e) => handleCheckboxChange('solarEnabled', e)} />
            }
            label="Solar"
          />
          <br />
          <FormControlLabel
            className="checkbox-row"
            control={
              <Checkbox
                checked={simulation.freeViewEnabled}
                onChange={(e) => handleCheckboxChange('freeViewEnabled', e)}
              />
            }
            label="Free-view"
          />
          <br />
          <FormControlLabel
            className="checkbox-row"
            control={
              <Checkbox
                checked={simulation.targetViewEnabled}
                onChange={(e) => handleCheckboxChange('targetViewEnabled', e)}
              />
            }
            label="Target-view"
          />
          <br />
          <FormControlLabel
            className="checkbox-row"
            control={
              <Checkbox checked={simulation.noiseEnabled} onChange={(e) => handleCheckboxChange('noiseEnabled', e)} />
            }
            label="Noise"
          />
          <br />
          <FormControlLabel
            className="checkbox-row"
            control={
              <Checkbox checked={simulation.windEnabled} onChange={(e) => handleCheckboxChange('windEnabled', e)} />
            }
            label="Wind"
          />
          {shouldShowCustomSettingsSelectionError && (
            <p className="custom-error-message-selection">Select at least one simulation to continue.</p>
          )}
        </>
      )}
      <br />
    </>
  );
};

export default Name;
